import React, { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import {
  HiOutlineMail,
  HiOutlineSave,
  HiOutlineKey,
  HiOutlineShieldCheck,
  HiOutlineServer,
  HiOutlineInformationCircle,
} from 'react-icons/hi';
import { settingsApi } from '../../services/api';

const GMAIL_SMTP_HOST = 'smtp.gmail.com';
const GMAIL_SMTP_PORT = '587';
const GMAIL_SMTP_SECURE = false;

const EmailSettings = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [smtpUser, setSmtpUser] = useState('');
  const [smtpPass, setSmtpPass] = useState('');
  const [passConfigured, setPassConfigured] = useState(false);
  const [passMasked, setPassMasked] = useState('');

  const load = async () => {
    setLoading(true);
    try {
      const res = await settingsApi.getEmail();
      const d = res.data.data || {};
      setSmtpUser(d.smtp_user || '');
      setSmtpPass('');
      setPassConfigured(Boolean(d.smtp_pass_configured));
      setPassMasked(d.smtp_pass_masked || '');
    } catch (e) {
      toast.error(e.response?.data?.message || 'Failed to load settings');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const handleSave = async (e) => {
    e.preventDefault();
    const user = smtpUser.trim();
    if (!user) {
      toast.error('Enter your Gmail address');
      return;
    }
    if (!passConfigured && !smtpPass.trim()) {
      toast.error('Enter your Gmail app password');
      return;
    }
    setSaving(true);
    try {
      const payload = {
        smtp_host: GMAIL_SMTP_HOST,
        smtp_port: GMAIL_SMTP_PORT,
        smtp_user: user,
        smtp_secure: GMAIL_SMTP_SECURE,
        smtp_from: user,
      };
      if (smtpPass.trim()) {
        payload.smtp_pass = smtpPass.trim();
      }
      await settingsApi.updateEmail(payload);
      toast.success('Gmail settings saved');
      setSmtpPass('');
      await load();
    } catch (e) {
      toast.error(e.response?.data?.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="w-full min-h-[40vh] flex flex-col items-center justify-center gap-2">
        <div className="h-9 w-9 rounded-full border-2 border-[#425F97] border-t-transparent animate-spin" />
        <p className="text-xs text-gray-500">Loading email settings…</p>
      </div>
    );
  }

  return (
    <div className="w-full max-w-[1400px] mx-auto space-y-4 pb-4 animate-fade-in">
      {/* Full-width hero */}
      <div className="relative overflow-hidden rounded-xl bg-gradient-to-br from-[#425F97] via-[#4d6ba8] to-slate-900 shadow-lg">
        <div
          className="pointer-events-none absolute inset-0 opacity-30"
          style={{
            backgroundImage:
              'radial-gradient(circle at 20% 20%, rgba(255,255,255,0.15) 0%, transparent 45%), radial-gradient(circle at 80% 60%, rgba(255,255,255,0.08) 0%, transparent 40%)',
          }}
        />
        <div className="relative px-4 py-5 md:px-6 md:py-6 lg:flex lg:items-end lg:justify-between lg:gap-4">
          <div className="max-w-2xl">
            <div className="inline-flex items-center gap-1.5 rounded-full bg-white/10 px-2.5 py-0.5 text-[10px] font-medium uppercase tracking-wider text-white/90 ring-1 ring-white/20 mb-2">
              <HiOutlineMail className="w-3.5 h-3.5" />
              Configuration
            </div>
            <h1 className="font-display text-xl md:text-2xl font-bold text-white tracking-tight">
              Gmail for password reset
            </h1>
            <p className="mt-1.5 text-xs md:text-sm text-white/85 leading-relaxed">
              One place to connect your clinic Gmail so users can receive OTP codes when they forget
              their password. Uses Google&apos;s standard SMTP — no extra fields required.
            </p>
          </div>
          <div className="mt-3 lg:mt-0 shrink-0">
            <div
              className={`inline-flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-xs font-semibold shadow ${
                passConfigured
                  ? 'bg-emerald-500/95 text-white ring-2 ring-emerald-300/50'
                  : 'bg-amber-500/95 text-white ring-2 ring-amber-200/50'
              }`}
            >
              <span
                className={`h-2 w-2 rounded-full ${passConfigured ? 'bg-white animate-pulse' : 'bg-white'}`}
              />
              {passConfigured ? 'App password on file' : 'Not configured yet'}
            </div>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-12 gap-3 lg:gap-4 items-start">
        {/* Left: info + server pills — full width on mobile, 4 cols on xl */}
        <div className="xl:col-span-4 space-y-3">
          <div className="rounded-xl border border-gray-200/90 bg-white p-4 shadow-sm">
            <div className="flex items-start gap-2.5">
              <div
                className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg text-sm font-bold text-white shadow-sm"
                style={{ backgroundColor: '#EA4335' }}
                aria-hidden
              >
                G
              </div>
              <div>
                <h2 className="text-sm font-semibold text-gray-900">Why Gmail?</h2>
                <p className="mt-1 text-xs text-gray-600 leading-relaxed">
                  Google App Passwords are the safest way to let this app send mail without storing
                  your main account password.
                </p>
              </div>
            </div>
          </div>

          <div className="rounded-xl border border-gray-200/90 bg-gradient-to-b from-slate-50 to-white p-4 shadow-sm">
            <div className="flex items-center gap-2 text-xs font-semibold text-gray-900 mb-3">
              <HiOutlineServer className="w-4 h-4 text-[#425F97]" />
              Server (read-only)
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              <div className="rounded-lg border border-gray-200 bg-white px-3 py-2">
                <p className="text-[10px] font-medium uppercase tracking-wide text-gray-400">Host</p>
                <p className="mt-1 font-mono text-xs text-gray-900 break-all">{GMAIL_SMTP_HOST}</p>
              </div>
              <div className="rounded-lg border border-gray-200 bg-white px-3 py-2">
                <p className="text-[10px] font-medium uppercase tracking-wide text-gray-400">Port</p>
                <p className="mt-1 font-mono text-xs text-gray-900">{GMAIL_SMTP_PORT}</p>
              </div>
            </div>
          </div>

          <div className="rounded-xl border border-amber-100 bg-amber-50/80 p-4 shadow-sm">
            <div className="flex gap-2.5">
              <HiOutlineInformationCircle className="w-4 h-4 shrink-0 text-amber-700 mt-0.5" />
              <div className="text-xs text-amber-950/90">
                <p className="font-semibold text-amber-900">Create app password</p>
                <ol className="mt-1.5 list-decimal list-inside space-y-1 text-amber-900/85">
                  <li>Google Account → Security</li>
                  <li>Enable 2-Step Verification</li>
                  <li>App passwords → Mail / Other → generate</li>
                  <li>Paste the 16-character code below</li>
                </ol>
              </div>
            </div>
          </div>
        </div>

        {/* Right: form — full width on mobile, 8 cols on xl */}
        <div className="xl:col-span-8">
          <form
            onSubmit={handleSave}
            className="rounded-xl border border-gray-200/90 bg-white p-4 md:p-5 lg:p-6 shadow-sm ring-1 ring-black/[0.03]"
          >
            <div className="flex items-center gap-2.5 mb-4 pb-3 border-b border-gray-100">
              <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-[#425F97]/10 text-[#425F97]">
                <HiOutlineShieldCheck className="w-5 h-5" />
              </div>
              <div>
                <h2 className="text-base font-bold text-gray-900">Credentials</h2>
                <p className="text-xs text-gray-500">Only clinic Gmail and app password</p>
              </div>
            </div>

            <div className="space-y-4 max-w-3xl">
              <div>
                <label className="block text-xs font-semibold text-gray-800 mb-1.5">
                  Gmail address
                </label>
                <div className="relative">
                  <HiOutlineMail className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
                  <input
                    type="email"
                    value={smtpUser}
                    onChange={(e) => setSmtpUser(e.target.value)}
                    className="w-full rounded-lg border border-gray-200 bg-gray-50/50 py-2.5 pl-9 pr-3 text-sm text-gray-900 placeholder:text-gray-400 transition focus:border-[#425F97] focus:bg-white focus:outline-none focus:ring-2 focus:ring-[#425F97]/25"
                    placeholder="clinic@gmail.com"
                    autoComplete="username"
                  />
                </div>
              </div>

              <div>
                <label className="block text-xs font-semibold text-gray-800 mb-1.5">
                  Gmail app password
                </label>
                <div className="relative">
                  <HiOutlineKey className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
                  <input
                    type="password"
                    value={smtpPass}
                    onChange={(e) => setSmtpPass(e.target.value)}
                    className="w-full rounded-lg border border-gray-200 bg-gray-50/50 py-2.5 pl-9 pr-3 text-sm text-gray-900 placeholder:text-gray-400 transition focus:border-[#425F97] focus:bg-white focus:outline-none focus:ring-2 focus:ring-[#425F97]/25"
                    placeholder={
                      passConfigured
                        ? 'Leave blank to keep the saved app password'
                        : 'Paste the 16-character app password'
                    }
                    autoComplete="new-password"
                  />
                </div>
                {passConfigured && (
                  <p className="mt-1.5 flex items-center gap-1.5 text-[11px] text-gray-500">
                    <span className="inline-flex h-1.5 w-1.5 rounded-full bg-emerald-500" />
                    Saved: <span className="font-mono text-gray-700">{passMasked || '••••••••'}</span>
                  </p>
                )}
              </div>

              <div className="flex flex-wrap items-center gap-2.5 pt-1">
                <button
                  type="submit"
                  disabled={saving}
                  className="inline-flex min-h-[40px] items-center justify-center gap-2 rounded-lg bg-[#425F97] px-5 py-2 text-sm font-semibold text-white shadow transition hover:bg-[#384f7d] focus:outline-none focus:ring-2 focus:ring-[#425F97] focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-60"
                >
                  <HiOutlineSave className="h-4 w-4" />
                  {saving ? 'Saving…' : 'Save settings'}
                </button>
                <p className="text-xs text-gray-500 max-w-md">
                  After saving, forgot-password emails will use this mailbox until you change it.
                </p>
              </div>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default EmailSettings;
